<template>
	<svg class="editor-timeline"/>
</template>

<script>
export default {
	name: "MediaEditorTimeline",
	props: {
		duration: {
			type: Number,
			required: true
		},
		markInPos: {
			type: Number,
			required: true
		},
		markOutPos: {
			type: Number,
			required: true
		},
		playerPosition: {
			type: Number,
			required: true
		}
	},
	watch: {
		playerPosition(val) {
			this.markerPosition = val;
			this.updateMarkerPosition();
		}
	},
	created() {
		this.dialogWidth = null;
		this.dialogHeight = null;
		this.markerPosition = this.playerPosition;
	},
	mounted() {
		this.editorElement = this.$parent.$el.querySelector(".ui-dialog__content .editor");
		this.init(this.editorElement.offsetWidth);
		addEventListener("resize", this.onResize);
	},
	beforeDestroy() {
		removeEventListener("resize", this.onResize);
	},
	methods: {
		init(playerWidth) {
			// init all variables
			this.svg = d3.select(this.$el)
			.attr("width", playerWidth)
			.attr("height", 40);

			this.rangeMargin = { top: 0, right: 18, bottom: 0, left: 18 };
			this.rangeWidth = playerWidth - this.rangeMargin.left - this.rangeMargin.right;
			this.rangeHeight = 20;

			this.rangeXScale = d3.scaleLinear()
			.range([0, this.rangeWidth])
			.domain([0, this.duration])
			.nice([0, this.duration]);

			this.rangeXAxis = d3.axisBottom(this.rangeXScale)
			.tickValues(this.getTickValues(this.rangeWidth))
			.tickFormat((d) => this.$options.filters.timecode(d));

			this.brush = d3.brushX().extent([[0, 0], [this.rangeWidth, this.rangeHeight]]).handleSize(20);

			// add range element
			this.rangeElement = this.svg.append("g")
			.attr("class", "range")
			.attr("transform", `translate(${this.rangeMargin.left},${this.rangeMargin.top})`);

			// add axis element where the X axis is drawn
			this.axisElement = this.rangeElement.append("g")
			.attr("class", "axis axis--x")
			.attr("transform", `translate(0,${this.rangeHeight})`)
			.call(this.rangeXAxis);

			// add click layer for moving current position marker to clicked position
			this.currentPositionClickLayerElement = this.rangeElement.append("rect")
			.attr("class", "current-position-click-layer")
			.attr("width", this.rangeWidth)
			.attr("height", this.rangeHeight + 18)
			.on("click", (d, i, nodes) => {
				const x = d3.mouse(nodes[i])[0];
				this.updateMarkerPosition(x);
				this.$emit("goToPosition", this.rangeXScale.invert(x));
			});

			// add brush element, this brush is used as the range selector
			this.brushElement = this.rangeElement.append("g")
			.attr("class", "brush")
			.call(this.brush);

			// remove overlay that is automatically added and used by d3 for creating new brush by click and drag inside range area
			// this interferes with clicking in the range area to move current position marker
			this.rangeElement.select(".overlay").remove();

			// add the current position marker, which is updated dynamically linked to player position
			// and emits position to player when moved with mouse events
			this.currentPositionMarker = this.rangeElement.append("circle")
			.attr("class", "current-position-marker")
			.attr("cx", 0)
			.attr("cy", this.rangeHeight)
			.attr("r", 5)
			.call(
				d3.drag()
				.on("drag end", () => {
					let x = null;
					if (d3.event.x < 0)
						x = 0;
					else if (d3.event.x > this.rangeWidth)
						x = this.rangeWidth;
					else
						x = d3.event.x;

					this.updateMarkerPosition(x);
					this.$emit("goToPosition", this.rangeXScale.invert(x));
				})
			);

			// set default range (5% after start and 5% before end)
			this.rangeElement.select(".brush").call(
				this.brush.move,
				[this.rangeXScale(this.duration * 5 / 100), this.rangeXScale(this.duration * 95 / 100)]
			);

			// get current brush selection
			const brushSelection = d3.brushSelection(this.brushElement.node());

			// set current mark in/out points from current brush selection and emit these to parent
			this.markInOutRange = [this.rangeXScale.invert(brushSelection[0]), this.rangeXScale.invert(brushSelection[1])];
			this.$emit("update:markInPos", this.markInOutRange[0]);
			this.$emit("update:markOutPos", this.markInOutRange[1]);

			// add custom brush handles
			this.brushElement.selectAll(".handle--custom")
			.data([{ type: "w" }, { type: "e" }])
			.enter()
			.append("g")
			.attr("class", (d) => { return d.type === "w" ? "handle--custom handle--custom--w" : "handle--custom handle--custom--e"; })
			.append("path")
			.attr("d", (d) => {
				const x = d.type === "w" ? 1 : -1;
				const y = this.rangeHeight;
				const ld = y / 3;

				// this path is the left/right limit symbol
				return `M 0 0 l 0 ${ld} ${-(x * (ld/2))} ${ld/2} ${x * ld/2} ${ld/2} 0 ${ld}`;
			});

			// move custom handles to initial positions of brush
			this.brushHandles = this.rangeElement.select(".brush")
			.selectAll(".handle--custom")
			.attr("transform", (d) => `translate(${d.type === "w" ? brushSelection[0] : brushSelection[1]})`);

			// store brush start coords to use for which brush handle moved detection
			this.brushStart = [];

			// event triggering when moving range brush
			this.brush.on("start brush end", () => {
				const s = d3.event.selection || this.rangeXScale.range();

				if (d3.event.sourceEvent && (
				    d3.event.sourceEvent.type === "mousedown" ||
				    d3.event.sourceEvent.type === "mousemove" ||
				    d3.event.sourceEvent.type === "mouseup" ||
				    d3.event.sourceEvent.type === "touchstart" ||
				    d3.event.sourceEvent.type === "touchmove" ||
				    d3.event.sourceEvent.type === "touchend")
				) {
					// move position marker to start/end brush handle when adjusting start/end
					if (d3.event.type === "start") {
						this.brushStart = d3.event.selection || this.rangeXScale.range();
					}
					else if (d3.event.type === "brush" || d3.event.type === "end") {
						// determine which handle is dragged and move position marker to the dragged handle
						if (this.brushStart[0] === s[0] && this.brushStart[1] !== s[1]) {
							this.updateMarkerPosition(s[1]);
							this.$emit("goToPosition", this.rangeXScale.invert(s[1]));
						}
						else {
							this.updateMarkerPosition(s[0]);
							this.$emit("goToPosition", this.rangeXScale.invert(s[0]));
						}
					}

					// update in / out marks array (this should always hold "real" positions which are later passed to API)
					this.markInOutRange = [this.rangeXScale.invert(s[0]), this.rangeXScale.invert(s[1])];
					this.$emit("update:markInPos", this.markInOutRange[0]);
					this.$emit("update:markOutPos", this.markInOutRange[1]);
				}

				// move custom brush handles
				this.rangeElement.select(".brush")
				.selectAll(".handle--custom")
				.attr("transform", (d, i) => `translate(${s[i]})`);
			});

			// update marker position in case playerPosition watcher is set before initialization of timeline
			this.updateMarkerPosition();
		},
		getTickValues(rangeWidth) {
			const maxTicksInRange = Math.floor(rangeWidth / 60); // 60 here is pixels between ticks, which is min width to not make labels overlap
			const maxTickInterval = Math.max(1, Math.floor(this.duration / maxTicksInRange));
			const tickIntervals = [1, 2, 5, 10, 20, 30, 60, 120, 300, 600, 900, 1200, 1800, 3600];

			let tickInterval = 7200;

			for (let i = 0; i < tickIntervals.length; i++) {
				if (maxTickInterval <= tickIntervals[i]) {
					tickInterval = tickIntervals[i];
					break;
				}
			}

			const tickValues = [...d3.range(0, this.duration, tickInterval), this.duration]; // create range with tickInterval + precise duration

			// drop second last tick value if difference between last and second last is less than half a tick interval value
			// to prevent labels from overlapping
			if (tickValues[tickValues.length - 1] - tickValues[tickValues.length - 2] < tickInterval / 2)
				tickValues.splice(tickValues.length - 2, 1);

			return tickValues;
		},
		markIn() {
			const brushElement = this.svg.select(".brush");
			const brushSelection = d3.brushSelection(brushElement.node());
			const position = this.rangeXScale(Math.min(this.markerPosition, this.duration));

			if (position === this.markInOutRange[1] || position === this.rangeWidth)
				return;

			if (brushSelection && position < brushSelection[1])
				brushElement.call(this.brush.move, [position, brushSelection[1]]);
			else {
				brushElement.call(this.brush.move, [position, this.rangeWidth]);
				this.$emit("update:markOutPos", this.rangeWidth);
			}

			this.$emit("update:markInPos", this.markerPosition);

			this.markInOutRange = d3.brushSelection(brushElement.node()); // brush selection has changed, get new values from d3.brushSelection
		},
		markOut() {
			const brushElement = this.svg.select(".brush");
			const brushSelection = d3.brushSelection(brushElement.node());
			const position = this.rangeXScale(Math.min(this.markerPosition, this.duration));

			if (position === this.markInOutRange[0] || position === 0)
				return;

			if (brushSelection && position > brushSelection[0])
				brushElement.call(this.brush.move, [brushSelection[0], position]);
			else {
				brushElement.call(this.brush.move, [0, position]);
				this.$emit("update:markInPos", 0);
			}

			this.$emit("update:markOutPos", this.markerPosition);

			this.markInOutRange = d3.brushSelection(brushElement.node()); // brush selection has changed, get new values from d3.brushSelection
		},
		onResize() {
			this.$nextTick(() => {
				const width = this.editorElement.offsetWidth;
				const height = this.editorElement.offsetHeight;

				if (this.dialogWidth !== width || this.dialogHeight !== height) {
					this.dialogWidth = width;
					this.dialogHeight = height;

					this.redraw(width);
				}
			});
		},
		redraw(playerWidth) {
			this.rangeWidth = playerWidth - this.rangeMargin.left - this.rangeMargin.right;
			this.svg.attr("width", playerWidth);
			this.rangeXScale.range([0, this.rangeWidth]);
			this.rangeXAxis.tickValues(this.getTickValues(this.rangeWidth));
			this.currentPositionClickLayerElement.attr("width", this.rangeWidth);
			this.axisElement.call(this.rangeXAxis);
			this.brushElement.call(this.brush);
			this.rangeElement.select(".overlay").remove(); // overlay is again added by d3 when calling brush, remove it
			this.updateMarkerPosition();
			this.brushElement.call(this.brush.move, [this.rangeXScale(this.markInOutRange[0]), this.rangeXScale(this.markInOutRange[1])]);
			const brushSelection = d3.brushSelection(this.brushElement.node());
			this.brushHandles.attr("transform", (d) => `translate(${d.type === "w" ? brushSelection[0] : brushSelection[1]})`);
		},
		updateMarkerPosition(pos) {
			if (typeof pos === "undefined")
				pos = this.rangeXScale(Math.min(this.markerPosition, this.duration));
			else
				this.markerPosition = this.rangeXScale.invert(pos);

			this.svg.select(".range .current-position-marker")
			.attr("transform", `translate(${pos})`);
		}
	}
};
</script>

<style lang="scss" scoped>
.editor-timeline {
	margin-top: 16px;
	/deep/ .range {
		.axis {
			overflow: overlay;
			font: 10px "Roboto", Arial, Helvetica, sans-serif;
			path,
			line {
				fill: none;
				stroke: rgba(0, 0, 0, 0.54);
				shape-rendering: crispEdges;
			}
			.tick {
				text {
					cursor: default;
				}
			}
		}
		.current-position-click-layer {
			fill: none;
			pointer-events: visible;
		}
		.current-position-marker {
			cursor: pointer;
			fill: #757575;
		}
		.brush {
			.handle--custom {
				path {
					stroke: #757575;
					stroke-width: 1.4;
					stroke-linejoin: round;
				}
				cursor: ew-resize;
			}
			.selection {
				fill: #64b5f6; // blue 300
				fill-opacity: 1;
				stroke: none;
				pointer-events: none; // disable possibility to move the entire brush
			}
		}
	}
}
</style>
